import { UserService } from "services/user.service";
import { OrganizationService } from "./../services/organization.service";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LoginUserDetail } from "models/LoginUserDetail";
import { RootState } from "./store";
import { Shared } from "utility/shared";
import { IMenuAccessRightModel } from "models/MenuAccessRightModel";
import { OrganizationDetail } from "models/OrganizationDetail";
import { AppRoutings } from "utility/enums/app-routings";

export const defaultRegionGridLabel = "Region";
export const defaultDivisionGridLabel = "Division";

export interface AuthState {
	userDetails: LoginUserDetail;
	organizationDetails: OrganizationDetail;
	userMenuAccessRights: IMenuAccessRightModel[];
	gridLabel: {
		region: string;
		division: string;
	};
}

const initialState: AuthState = {
	userDetails: {} as LoginUserDetail,
	organizationDetails: {} as OrganizationDetail,
	userMenuAccessRights: [],
	gridLabel: {
		region: "",
		division: "string;",
	},
};

export const fetchOrganizationDetail = createAsyncThunk(
	"getOrganizationDetails",
	async (isForceAPICall: boolean, { getState }) => {
		const state = getState() as RootState;
		if (!state.authState.organizationDetails.organizationId || isForceAPICall) {
			return await OrganizationService.getOrganizationDetails();
		}
	}
);

export const fetchUserMenuAccessRights = createAsyncThunk(
	"getUserMenuAccessRights",
	async (isForceAPICall: boolean, { getState }) => {
		const state = getState() as RootState;
		if (state.authState.userMenuAccessRights.length === 0 || isForceAPICall) {
			return await UserService.getUserMenuAccessRights();
		}
	}
);

export const authStateSlice = createSlice({
	name: "authState",
	initialState,
	reducers: {
		setLoggedInUserDetail: (state, action) => {
			state.userDetails = action.payload as LoginUserDetail;
		},
		setOrganizationDetail: (state, action) => {
			state.organizationDetails = action.payload as OrganizationDetail;
			state.gridLabel =
				action.payload &&
				action.payload.glAccountSettings &&
				action.payload.glAccountSettings.length > 0
					? {
							region:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "region"
								)?.label || defaultRegionGridLabel,
							division:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "division"
								)?.label || defaultDivisionGridLabel,
					  }
					: {
							region: defaultRegionGridLabel,
							division: defaultDivisionGridLabel,
					  };
		},
	},
	extraReducers: {
		[fetchOrganizationDetail.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.organizationId) {
				state.organizationDetails = action.payload;
				state.gridLabel =
					action.payload &&
					action.payload.glAccountSettings &&
					action.payload.glAccountSettings.length > 0
						? {
								region:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "region"
									)?.label || defaultRegionGridLabel,
								division:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "division"
									)?.label || defaultDivisionGridLabel,
						  }
						: {
								region: defaultRegionGridLabel,
								division: defaultDivisionGridLabel,
						  };
				localStorage.setItem(
					Shared.localStorageKeys.organizationDetails,
					JSON.stringify(action.payload)
				);
			}
		},
		[fetchOrganizationDetail.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading organization detail, please try again!"
			);
		},
		[fetchUserMenuAccessRights.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.userMenuAccessRights = action.payload;
			}
		},
		[fetchUserMenuAccessRights.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching user menu access right list, please try again!"
			);
			// added timeout to display snackbar message before redirection
			setTimeout(() => {
				window.location.href = AppRoutings.SignIn;
			}, 1000);
		},
	},
});

export const { setLoggedInUserDetail, setOrganizationDetail } =
	authStateSlice.actions;

export default authStateSlice.reducer;
